import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCSwitch, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import Title from "src/components/Title";
import Audit from "src/components/Audit";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import ModalSide from "src/components/Modal/styles";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { IClassification, IComponentSectorModals, IRefSectorModals } from "./interface";
import addAndChangeSchema from "./schemas";

const Modals: React.ForwardRefRenderFunction<IRefSectorModals, IComponentSectorModals> = ({
	paginationRef
}, ref) => {
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();
	const [selectedClassification, setSelectedClassification] = useState<IClassification>({} as IClassification);

	const actionButtonDelete = (row: IClassification) => {
		confirm(
			translate("Exclusão de classificação"),
			`${translate("Deseja realmente excluir a classificação")}: ${selectedClassification.taa002Descricao} ?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "Classification/Delete",
					urlParameters: {
						id: row.taa002Id
					}
				});
	
				addToast({
					color: "green",
					description: translate("Classificação excluída com sucesso"),
					title: translate("Sucesso")
				});

				paginationRef.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir classificação"), addToast);
			},
			true
		);
	};
	
	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const { handleSubmit : handleSubmitAddOrChange, register : registerAddOrChange, reset : resetAddOrChange, setError : setErrorAddOrChange, formState: { errors : errorsAddOrChange }, clearErrors: clearErrorsAddOrChange } = useForm();
	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();
			await addAndChangeSchema(translate).validate(data, {
				abortEarly: false
			});
			
			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "Classification/Insert",
					body: {
						...data
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: "Classification/Update",
					body: {
						...data,
						taa002Id: selectedClassification.taa002Id
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Classificação adicionada com sucesso") : translate("Classificação alterada com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef.current?.reload();

		}catch(err){

			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange))
				return;

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar classificação") : translate("Erro ao alterar classificação"), addToast);
		}
	};
	const actionButtonAdd = () => {
		resetAddOrChange(); 
		setSelectedClassification({taa002Ativo:true} as IClassification); 
		setIsAdd(true); 
		modalAddOrChange.current?.openModal();
	};
	const actionButtonChange = (row: IClassification) => {
		resetAddOrChange(); 
		setSelectedClassification(row); 
		setIsAdd(false); 
		modalAddOrChange.current?.openModal(); 
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));
    
	return (
		<>
			{/* Modal ADD/CHANGE */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar classificação") : translate("Alterar classificação"),
					icon: {
						name: "building"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
						alignItems="center"
						justify="space-between"
					>
						<Title color="purple" size="md">
						    {translate("Dados Gerais")}
						</Title>
						<Audit data={selectedClassification}/>
						<hr />
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCInput 
							register={registerAddOrChange}
							name="taa002Descricao"
							label={translate("Descrição")}
							maxLength={60}
							defaultValue={selectedClassification.taa002Descricao}
							error={errorsAddOrChange.taa002Descricao}
							required
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCGrid
							container
							item
							xs={12}
							sm={6}
							md={4}
							lg={3}
						>
							<RCCSwitch name="taa002Ativo" label={translate("Ativo")} register={registerAddOrChange} 
								defaultChecked ={selectedClassification.taa002Ativo}
							/>
						</RCCGrid>
					</RCCGrid>
				</RCCGrid>
			</Modal>
		</>
	);
};

export default forwardRef(Modals);
